import { createFileRoute, redirect, useNavigate } from "@tanstack/react-router";
import { Trans, useTranslation } from "react-i18next";
import {
  Footer,
  Header,
  Page,
  PageContent,
  BackButton,
} from "@/components/page.tsx";
import { formatCurrency } from "@/lib/utils.ts";
import { useShop } from "@/hooks/use-shop.ts";
import { usePaymentStatus } from "@/hooks/use-payment-status";
import { useUser } from "@/hooks/use-user.ts";
import { useMutation } from "@tanstack/react-query";
import { useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { ErrorModal } from "@/lib/api";
import { LoadingSpinner } from "@/components/spinner";
import { useMuiModal } from "@/components/modal";
import { api } from "@/lib/server";
import {
  BalanceTopupPayoutFailedModal,
  PrinterErrorModal,
} from "@/features/payment-cash/modals";
import { recordTrackedEvent } from "@/lib/kiosk-session-tracking";
import {
  clearBalanceTopupPaymentKey,
  getBalanceTopupPaymentKey,
} from "@/features/payment/utils";
import { featureFlags } from "@common/feature-flags";
import { AudioSlot } from "@/features/audio/kiosk-audio";
import { getFeatureFlags } from "@/lib/runtime-config";
import {
  AcceptedCashDisplay,
  CashImageCredits,
} from "@/features/payment-cash/accepted-cash-display";
import { LegacyAcceptedCashDisplay } from "@/features/payment-cash/legacy-accepted-cash-display";

export const Route = createFileRoute("/balance-replenish-cash-screen")({
  component: RouteComponent,
  beforeLoad: ({ context }) => {
    context.queryClient.invalidateQueries({ queryKey: ["user"] });
  },
  loader: () => {
    const token = localStorage.getItem("auth_token");
    const paymentKey = getBalanceTopupPaymentKey();
    if (!token || !paymentKey) {
      throw redirect({
        to: "/start-screen",
        reloadDocument: true,
      });
    }
    return { token, paymentKey };
  },
});

function RouteComponent() {
  const { t } = useTranslation();
  const { data: shop } = useShop();
  const {
    data: paymentStatus,
    isPending: isPaymentStatusPending,
    error,
  } = usePaymentStatus();
  const { data: user, refetch: refetchUser } = useUser();
  const navigate = useNavigate();
  const { showModal } = useMuiModal();
  const printErrorShownRef = useRef(false);
  const payoutWarningShownRef = useRef(false);
  const supportPhone = getFeatureFlags().supportPhone;

  const finishHopperTopUp = useFinishHopperTopUp();

  const navigateBack = () => {
    clearBalanceTopupPaymentKey();
    navigate({
      to: "/machine-type-selector-screen",
    });
  };

  useEffect(() => {
    if (paymentStatus?.printFailed && !printErrorShownRef.current) {
      printErrorShownRef.current = true;
      showModal(<PrinterErrorModal transactionId={paymentStatus.sessionId} />);
    }
  }, [paymentStatus?.printFailed, showModal]);

  useEffect(() => {
    if (paymentStatus) {
      switch (paymentStatus.paymentStatus) {
        case "RECENTLY_FINISHED":
        case "TIMED_OUT": {
          void new Promise((resolve) => setTimeout(resolve, 0))
            .then(() => refetchUser())
            .then(() => {
              navigateBack();
            });
          break;
        }
        case "ABORTED": {
          navigateBack();
          break;
        }
        case "ABORTED_BUT_PAYOUT_FAILED": {
          if (payoutWarningShownRef.current) {
            break;
          }

          payoutWarningShownRef.current = true;
          void showModal(
            <BalanceTopupPayoutFailedModal
              aborted
              supportPhone={supportPhone}
              onClose={navigateBack}
            />,
          );
          break;
        }
        case "SUCCESS_BUT_PAYOUT_FAILED": {
          if (payoutWarningShownRef.current) {
            break;
          }

          payoutWarningShownRef.current = true;
          void showModal(
            <BalanceTopupPayoutFailedModal
              aborted={false}
              supportPhone={supportPhone}
              onClose={navigateBack}
            />,
          );
          break;
        }
        case "HARDWARE_ERROR": {
          navigateBack();
          break;
        }
        case "TOP_UP_IN_PROGRESS":
        case "PAYMENT_IN_PROGRESS": {
          break;
        }
        default: {
          navigateBack();
          break;
        }
      }
    }
  }, [navigate, paymentStatus, refetchUser, showModal, supportPhone]);

  useEffect(() => {
    if (error) {
      showModal(<ErrorModal errorKey="server-message-internal-server-error" />);
      navigateBack();
    }
  }, [error, showModal]);

  if (finishHopperTopUp.data) {
    return (
      <Page>
        <AudioSlot slot="page.balance-replenish-cash.finishing" />
        <Header backArrowHref={null} />
        <PageContent>
          <h2 className={`font-helvetica font-bold text-6xl text-center mb-16`}>
            <Trans>Kérlek várj...</Trans>
          </h2>
        </PageContent>
      </Page>
    );
  }

  return (
    <Page className={`bg-background`} skipInactvityModal>
      <AudioSlot slot="page.balance-replenish-cash" />
      {isPaymentStatusPending && <LoadingSpinner />}
      <Header
        backArrowHref={
          (paymentStatus?.insertedAmount || 0) === 0 && (
            <BackButton
              label={t("Vissza")}
              onClick={() => finishHopperTopUp.mutate()}
            />
          )
        }
      />
      <PageContent>
        <h2 className={`font-helvetica font-bold text-6xl text-center mb-16`}>
          <Trans>Kérjük fizess!</Trans>
        </h2>

        {finishHopperTopUp.isPending && <LoadingSpinner />}
        <div className={`grid grid-cols-3 justify-center gap-x-5 mb-12`}>
          <div className={`text-center text-3xl`}>
            <p className={`mb-6`}>
              <Trans>Korábbi egyenleged</Trans>
            </p>
            <p className={`font-helvetica font-bold`}>
              {formatCurrency(user?.balance ?? 0, shop?.currencyCode ?? "HUF")}
            </p>
          </div>
          <div className={`text-center text-5xl`}>
            <p className={`mb-6`}>
              <Trans>Fizetve</Trans>
            </p>
            <p className={`font-helvetica font-bold`}>
              {formatCurrency(
                paymentStatus?.insertedAmount ?? 0,
                shop?.currencyCode ?? "HUF",
              )}
            </p>
          </div>
          <div className={`text-center text-3xl`}>
            <p className={`mb-6`}>
              <Trans>Új egyenleged</Trans>
            </p>
            <p className={`font-helvetica font-bold`}>
              {formatCurrency(
                (user?.balance ?? 0) + (paymentStatus?.insertedAmount ?? 0),
                shop?.currencyCode ?? "HUF",
              )}
            </p>
          </div>
        </div>

        {!!paymentStatus?.insertedAmount && (
          <div className={`flex justify-center mb-16`}>
            <Button
              className={`mx-auto`}
              onClick={() => finishHopperTopUp.mutate()}
              disabled={finishHopperTopUp.isPending}
            >
              <Trans>Végeztem</Trans>
            </Button>
          </div>
        )}
      </PageContent>

      {!featureFlags.kiosk.newCashUi ? (
        <LegacyAcceptedCashDisplay
          currencyCode={shop?.currencyCode ?? "HUF"}
          acceptedCoinValues={paymentStatus?.acceptedCoinValues ?? []}
          acceptedNoteValues={paymentStatus?.acceptedNoteValues ?? []}
        />
      ) : (
        paymentStatus && (
          <AcceptedCashDisplay
            currencyCode={shop?.currencyCode ?? "HUF"}
            acceptedCoinValues={paymentStatus.acceptedCoinValues}
            acceptedNoteValues={paymentStatus.acceptedNoteValues}
          />
        )
      )}

      <Footer
        attribution={
          featureFlags.kiosk.newCashUi &&
          paymentStatus && (
            <CashImageCredits
              currencyCode={shop?.currencyCode ?? "HUF"}
              showNotes
            />
          )
        }
        helpText={
          <Trans>
            Itt látod a jelenlegi egyenlegedet és a befizetett összeget. A
            befizetett pénz a Bubbles egyenlegedhez kerül hozzáadásra.
          </Trans>
        }
      />
    </Page>
  );
}

function useFinishHopperTopUp() {
  const { data: shop } = useShop();
  const navigate = useNavigate();
  const { showModal } = useMuiModal();
  return useMutation({
    mutationFn: async () => {
      const res = await api.finishHopperTopUp(
        shop.shopId,
        getBalanceTopupPaymentKey() ?? undefined,
      );
      if (res.type === "error") {
        showModal(<ErrorModal errorKey={res.error} />);
        return false;
      }
      await recordTrackedEvent({
        eventType: "balance_topup_finished",
        payload: { shopId: shop.shopId },
      });
      return true;
    },
    onError: () => {
      clearBalanceTopupPaymentKey();
      navigate({
        to: "/machine-type-selector-screen",
      });
    },
  });
}
